import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { handlePermissionClick } from '../../hooks/usePermissions';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, PowerOff, GraduationCap, LayoutGrid, List, Search, RotateCcw, Eye } from 'lucide-react';
import { RegistroMeta } from '../../components/admin/registro-meta';
import { ListAvatar } from '../../components/admin/list-avatar';
import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { useHorariosPorDia } from '../../api/hooks/useHorarios';
import { useAuthStore } from '../../store/authStore';
import { DataToolbar } from '../../components/ui/DataToolbar';
import { DataTable, PaginationFooter } from '../../components/ui/DataTable';
import { AlunoForm } from '../../components/forms/AlunoForm';
import { SetupPageHero } from '../../components/onboarding/SetupPageHero';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { hasPermission } from '../../auth/permissions';
import { AlunoFichaDesktop, AlunoFichaFooter } from './AlunoFichaDesktop';
import { api } from '../../api/client';
import { Modal } from '../../components/ui/Modal';
import { MobileProfessorAlunoPainel } from '../professor/MobileProfessorAlunoPainel';
import type { Aluno, Turma } from '../../types/models';
import '../../components/ui/datatable.css';
import { ROUTES } from '../../api/routes';

export function AlunosPage({
  readOnly = false,
  /** Desktop professor: olho abre modal analítico (não navega para ficha cadastral) */
  professorFichaModal = false,
}: {
  readOnly?: boolean;
  professorFichaModal?: boolean;
} = {}) {
  const { data: alunos, isLoading, error: loadError, deactivate, refetch } = useCrud<Aluno>('alunos');

  useEffect(() => {
    if (loadError) {
      toast.error('Não foi possível carregar os alunos. Tente atualizar a página.');
    }
  }, [loadError]);
  const { data: turmas } = useCrud<Turma>('turmas');
  const [formOpen, setFormOpen] = useState(false);
  const [editingAluno, setEditingAluno] = useState<Aluno | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filtroTurno, setFiltroTurno] = useState('');
  const [filtroTurma, setFiltroTurma] = useState('');
  const [viewMode, setViewMode] = useState<'lista' | 'cards'>('lista');
  const [pageSize, setPageSize] = useState(20);
  const [deleteConfirm, setDeleteConfirm] = useState<Aluno | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const escolaId = useAuthStore((s) => s.escolaId);
  const basePath = '/' + (location.pathname.split('/')[1] || 'admin');
  const isAdmin = basePath === '/admin';
  const canManage = !readOnly && hasPermission(user, 'alunos.manage');

  // === Ficha Cadastral (modal overlay — renderiza AlunoFichaPage completa) ===
  const [fichaId, setFichaId] = useState<string | null>(null);
  const [fichaAluno, setFichaAluno] = useState<Aluno | null>(null);
  const [transferAlunoId, setTransferAlunoId] = useState<string | null>(null);
  const [transferMotivo, setTransferMotivo] = useState('');
  const [transferEscola, setTransferEscola] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const isProfessor = basePath === '/professor';

  const openViewFicha = (id: string) => {
    if (professorFichaModal) {
      setFichaId(id);
      return;
    }
    if (isProfessor || readOnly) {
      navigate(`${basePath}/alunos/${id}`);
      return;
    }
    setFichaId(id);
    setFichaAluno(alunos?.find((a) => a.id === id) ?? null);
  };
  useEffect(() => {
    const state = location.state as { fichaId?: string } | null;
    if (professorFichaModal && state?.fichaId) {
      setFichaId(state.fichaId);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [professorFichaModal, location.pathname, location.state, navigate]);

  const [initialResponsavel, setInitialResponsavel] = useState<any | null>(null);

  useEffect(() => {
    const state = location.state as { prefilledResponsavel?: any } | null;
    if (state?.prefilledResponsavel) {
      setInitialResponsavel(state.prefilledResponsavel);
      setFormOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Buscar turmas do professor a partir dos horarios de todos os dias
  const seg = useHorariosPorDia(isProfessor ? user?.id : undefined, 'SEGUNDA');
  const ter = useHorariosPorDia(isProfessor ? user?.id : undefined, 'TERCA');
  const qua = useHorariosPorDia(isProfessor ? user?.id : undefined, 'QUARTA');
  const qui = useHorariosPorDia(isProfessor ? user?.id : undefined, 'QUINTA');
  const sex = useHorariosPorDia(isProfessor ? user?.id : undefined, 'SEXTA');
  const sab = useHorariosPorDia(isProfessor ? user?.id : undefined, 'SABADO');

  const professorTurmaIds = useMemo(() => {
    if (!isProfessor) return null;
    const ids = new Set<string>();
    [...seg.aulas, ...ter.aulas, ...qua.aulas, ...qui.aulas, ...sex.aulas, ...sab.aulas]
      .forEach((h: any) => { if (h.turmaId && h.tipo === 'AULA') ids.add(h.turmaId); });
    return ids;
  }, [isProfessor, seg.aulas, ter.aulas, qua.aulas, qui.aulas, sex.aulas, sab.aulas]);

  // Base de alunos: professor ve so os seus, admin ve todos
  const baseAlunos = useMemo(() => {
    if (!professorTurmaIds) return alunos;
    return alunos.filter(a => professorTurmaIds.has(a.turmaId || ''));
  }, [alunos, professorTurmaIds]);

  const openNew = () => { setEditingAluno(null); setFormOpen(true); };
  const openEdit = (a: Aluno) => { setEditingAluno(a); setFormOpen(true); };

  const requestDelete = (a: Aluno) => setDeleteConfirm(a);
  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const id = deleteConfirm.id;
    setDeleteConfirm(null);
    try {
      await deactivate.mutateAsync(id);
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      toast.success('Aluno desativado');
    } catch {
      toast.error('Erro ao desativar');
    }
  };

  const handleReactivate = async (a: Aluno) => {
    try {
      const { api } = await import('../../api/client');
      await api.patch(ROUTES.alunos.findOne(escolaId!, a.id) + '/reativar');
      toast.success(`"${a.nome}" reativado com sucesso!`);
      refetch();
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
    } catch {
      toast.error('Erro ao reativar aluno.');
    }
  };

  const formatDate = (d: string) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; }
  };
  const truncate = (value: string, max = 15) => {
    if (!value) return '—';
    return value.length > max ? `${value.slice(0, max)}...` : value;
  };

  // Helper para obter o turno (suporta legado e novo modelo operacional)
  const getTurno = (t: any) => t?.turnoOperacional?.nome || t?.turno || '';

  // Opções para os filtros
  const turnosUnicos = useMemo(() => {
    const turnos = new Set<string>();
    turmas.forEach(t => { 
      const turno = getTurno(t);
      if (turno) turnos.add(turno); 
    });
    return Array.from(turnos).sort();
  }, [turmas]);
  
  const turmasOptions = useMemo(() => {
    let t = turmas.filter(t => t.ativo);
    if (filtroTurno) t = t.filter(t => getTurno(t) === filtroTurno);
    if (professorTurmaIds) t = t.filter(t => professorTurmaIds.has(t.id));
    return t.sort((a, b) => a.nome.localeCompare(b.nome));
  }, [turmas, filtroTurno, professorTurmaIds]);

  // Filtrar dados
  const filteredAlunos = useMemo(() => {
    let result = baseAlunos;

    if (filtroTurno) {
      result = result.filter(a => getTurno(a.turma) === filtroTurno);
    }
    if (filtroTurma) {
      result = result.filter(a => a.turmaId === filtroTurma);
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter((a) =>
        [a.nome, a.matricula, a.turma?.nome, a.genero]
          .join(' ')
          .toLowerCase()
          .includes(q),
      );
    }
    return result;
  }, [baseAlunos, searchQuery, filtroTurno, filtroTurma]);

  const ativos = baseAlunos.filter(a => a.ativo).length;

  const columns: ColumnDef<Aluno>[] = useMemo(() => [
    {
      accessorKey: 'nome', header: 'Nome',
      cell: ({ row }) => (
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, opacity: row.original.ativo ? 1 : 0.4 }}>
          <ListAvatar fotoUrl={row.original.fotoUrl} nome={row.original.nome} role="ALUNO" size={44} />
          <div style={{ minWidth: 0 }}>
            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 700, fontSize: '.92rem' }}>{row.original.nome}</div>
            <div style={{ fontSize: '.76rem', color: '#2563eb', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.original.matricula || '—'}
            </div>
          </div>
        </div>
      ),
    },
    ...(!readOnly
      ? [{
          accessorKey: 'dataNascimento' as const,
          header: 'Nascimento',
          cell: ({ row }: { row: { original: Aluno } }) => (
            <span style={{ opacity: row.original.ativo ? 1 : 0.4 }}>
              {formatDate(row.original.dataNascimento)}
            </span>
          ),
        }]
      : []),
    {
      accessorKey: 'turma', header: 'Turma',
      cell: ({ row }) => <span style={{ opacity: row.original.ativo ? 1 : 0.4 }}>{truncate(row.original.turma?.nome || '—', 15)}</span>,
    },
    {
      accessorKey: 'genero', header: 'Gênero',
      cell: ({ row }) => {
        const g = row.original.genero;
        return <span style={{ opacity: row.original.ativo ? 1 : 0.4 }}>{g === 'MASCULINO' ? 'M' : g === 'FEMININO' ? 'F' : g || '—'}</span>;
      },
    },
    {
      accessorKey: 'ativo', header: 'Status',
      cell: ({ row }) => <span className={`badge ${row.original.ativo ? 'badge-active' : 'badge-inactive'}`}>{row.original.ativo ? 'Ativo' : 'Inativo'}</span>,
    },
    {
      id: 'registro',
      header: 'Registro',
      enableSorting: false,
      cell: ({ row }) => (
        <RegistroMeta
          criadoEm={row.original.criadoEm}
          atualizadoEm={row.original.atualizadoEm}
          atualizadoPor={row.original.atualizadoPor}
        />
      ),
    },
    {
      id: 'actions', header: '',
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            className="action-btn action-btn-view"
            title="Ver ficha"
            onClick={() => openViewFicha(row.original.id)}
          >
            <Eye size={15} />
          </button>
          <button
            className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
            title={!canManage ? 'Permissão restrita para edição' : 'Editar'}
            onClick={() => handlePermissionClick(canManage, () => openEdit(row.original), 'Você não tem permissão para editar alunos.')}
          >
            <Pencil size={15} />
          </button>
          {row.original.ativo ? (
            <button
              className={`action-btn action-btn-danger ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
              title={!canManage ? 'Permissão restrita para desativação' : 'Desativar'}
              onClick={() => handlePermissionClick(canManage, () => requestDelete(row.original), 'Você não tem permissão para desativar alunos.')}
            >
              <PowerOff size={15} />
            </button>
          ) : (
            <button
              className={`action-btn action-btn-success ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
              title={!canManage ? 'Permissão restrita para reativação' : 'Reativar'}
              onClick={() => handlePermissionClick(canManage, () => handleReactivate(row.original), 'Você não tem permissão para reativar alunos.')}
            >
              <RotateCcw size={15} />
            </button>
          )}
        </div>
      ),
    },
  ], [readOnly, canManage, alunos, openViewFicha]);

  // Turmas no dropdown: professor ve so as dele
  const turmasAtivas = useMemo(() => {
    const ativas = turmas.filter(t => t.ativo);
    if (!professorTurmaIds) return ativas;
    return ativas.filter(t => professorTurmaIds.has(t.id));
  }, [turmas, professorTurmaIds]);

  return (
    <div className="admin-page" style={{ marginTop: -24, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {isAdmin && !isLoading && (
        <SetupPageHero
          icon={GraduationCap}
          etapa={7}
          titulo="Alunos"
          descricao={
            turmas.filter(t => t.ativo).length === 0
              ? 'Para cadastrar alunos, primeiro crie as turmas da escola. Cada aluno será vinculado a uma turma.'
              : ativos === 0
                ? `Cadastre os alunos da escola. ${turmasAtivas.length} turma(s) disponível(is). Cada aluno será vinculado a uma turma e turno.`
                : 'Gerencie os alunos matriculados da escola. Atualize dados, turma e situação acadêmica.'
          }
          bloqueado={turmas.filter(t => t.ativo).length === 0}
          mensagemBloqueio={turmas.filter(t => t.ativo).length === 0 ? 'Crie Turmas antes de cadastrar Alunos' : undefined}
          acoes={[]}
          dismissivel={false}
          compact
        />
      )}
      <div className="ac-admin-data-card" style={{ background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <DataToolbar
          busca={searchQuery}
          onBuscaChange={setSearchQuery}
          placeholder="Buscar aluno..."
          filters={
            <>
              <select value={filtroTurno} onChange={e => { setFiltroTurno(e.target.value); setFiltroTurma(''); }} style={{ height: 34, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '0 10px', fontSize: '.8rem', background: 'var(--bg-card)', color: 'var(--text-main)', flexShrink: 0 }}>
                <option value="">Todos os Turnos</option>
                {turnosUnicos.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              
              <select value={filtroTurma} onChange={e => setFiltroTurma(e.target.value)} style={{ height: 34, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '0 10px', fontSize: '.8rem', background: 'var(--bg-card)', color: 'var(--text-main)', flexShrink: 0, maxWidth: 180, textOverflow: 'ellipsis' }}>
                <option value="">Todas as Turmas</option>
                {turmasOptions.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
              </select>
            </>
          }
          actions={
            <button
              type="button"
              data-testid="btn-cadastrar-aluno"
              className={`btn-primary ${!canManage ? 'opacity-50 cursor-not-allowed filter grayscale' : ''}`}
              onClick={() => handlePermissionClick(canManage, openNew, 'Você não tem permissão para cadastrar alunos.')}
              title={!canManage ? 'Permissão restrita a Diretores e Administradores' : 'Cadastrar novo aluno'}
              style={{ height: 34, padding: '0 12px', fontSize: '.8rem', display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
            >
              <Plus size={15} /> Cadastrar Aluno
            </button>
          }
          totalRecords={filteredAlunos.length}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[10, 20, 40, 100]}
          viewMode={viewMode}
          onViewModeChange={(m) => setViewMode(m as 'lista' | 'cards')}
          viewModes={['lista', 'cards']}
        />
        <div className="ac-admin-list-scroll" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', minHeight: 0 }}>
          {viewMode === 'lista' ? (
            <DataTable columns={columns} data={filteredAlunos} isLoading={isLoading} searchPlaceholder="Buscar aluno por nome ou matrícula..." showToolbar={false} pageSize={pageSize} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-2 sm:gap-3 p-2 sm:p-3">
              {filteredAlunos.map((a) => (
                <div key={a.id} className="border border-[var(--border-color,#e5e7eb)] rounded-xl p-2.5 sm:p-3 bg-[var(--bg-card,white)] shadow-sm hover:shadow transition-shadow flex flex-col justify-between">
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, gap: 8 }}>
                    <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                      <ListAvatar fotoUrl={a.fotoUrl} nome={a.nome} role="ALUNO" size={40} />
                      <div style={{ minWidth: 0 }}>
                        <div className="font-bold text-xs sm:text-sm text-[var(--text-main,#1e293b)] truncate">{a.nome}</div>
                        <div className="text-[11px] text-blue-600 dark:text-blue-400 truncate">{a.matricula || '—'}</div>
                      </div>
                    </div>
                    <span className={`badge ${a.ativo ? 'badge-active' : 'badge-inactive'} text-[9px] px-1.5 py-0.5 shrink-0`}>{a.ativo ? 'Ativo' : 'Inativo'}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mb-1.5 truncate">Turma: <span className="font-medium text-slate-700 dark:text-slate-300">{a.turma?.nome || '—'}</span> • Nasc: {formatDate(a.dataNascimento)}</div>
                  <div style={{ marginBottom: 6 }} className="scale-95 origin-left">
                    <RegistroMeta criadoEm={a.criadoEm} atualizadoEm={a.atualizadoEm} atualizadoPor={a.atualizadoPor} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, borderTop: '1px solid var(--border-color,#e5e7eb)/50', paddingTop: 6 }}>
                    <button
                      className="action-btn action-btn-view"
                      title="Ver ficha"
                      onClick={() => openViewFicha(a.id)}
                    >
                      <Eye size={13} />
                    </button>
                    <button
                      className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                      title={!canManage ? 'Permissão restrita para edição' : 'Editar'}
                      onClick={() => handlePermissionClick(canManage, () => openEdit(a), 'Você não tem permissão para editar alunos.')}
                    >
                      <Pencil size={13} />
                    </button>
                    {a.ativo ? (
                      <button
                        className={`action-btn action-btn-danger ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                        title={!canManage ? 'Permissão restrita para desativação' : 'Desativar'}
                        onClick={() => handlePermissionClick(canManage, () => requestDelete(a), 'Você não tem permissão para desativar alunos.')}
                      >
                        <PowerOff size={13} />
                      </button>
                    ) : (
                      <button
                        className={`action-btn action-btn-success ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                        title={!canManage ? 'Permissão restrita para reativação' : 'Reativar'}
                        onClick={() => handlePermissionClick(canManage, () => handleReactivate(a), 'Você não tem permissão para reativar alunos.')}
                      >
                        <RotateCcw size={13} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {filteredAlunos.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '.85rem', padding: 8 }}>Nenhum registro encontrado.</div>}
            </div>
          )}
          {viewMode === 'cards' && (
            <PaginationFooter
              totalItems={filteredAlunos.length}
              pageSize={pageSize}
            />
          )}
        </div>

      </div>

      <AlunoForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingAluno(null); setInitialResponsavel(null); }}
        editing={editingAluno}
        initialResponsavel={initialResponsavel}
        onCreated={(id) => {
          setFormOpen(false);
          setEditingAluno(null);
          setInitialResponsavel(null);
          window.dispatchEvent(new CustomEvent('ac-data-refetch'));
          refetch().then(() => setTimeout(() => setFichaId(id), 150));
        }}
      />

      {/* === Ficha professor (modal analítico — padrão lista + overlay escola) === */}
      <Modal
        open={!!fichaId && professorFichaModal}
        onClose={() => setFichaId(null)}
        title="Ficha do aluno"
        size="wide"
        contentClassName="ac-ficha-modal ac-professor-aluno-modal"
        hideCloseButton
        footer={
          <div className="ac-ficha-actions">
            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Somente consulta — dados cadastrais não editáveis</span>
            <span className="ac-ficha-action-spacer" />
            <button type="button" className="ac-ficha-action-btn" onClick={() => setFichaId(null)}>
              Fechar
            </button>
          </div>
        }
      >
        {fichaId ? <MobileProfessorAlunoPainel alunoId={fichaId} variant="modal" /> : null}
      </Modal>

      {/* === Ficha cadastral admin (Modal — padrão Responsáveis) === */}
      <Modal
        open={!!fichaId && !professorFichaModal && !isProfessor && !readOnly}
        onClose={() => { setFichaId(null); setFichaAluno(null); }}
        title="Ficha do aluno"
        size="wide"
        contentClassName="ac-ficha-modal ac-aluno-ficha-modal"
        hideCloseButton
        footer={
          fichaId && escolaId ? (
            <AlunoFichaFooter
              aluno={(fichaAluno ?? { id: fichaId, criadoEm: '', ativo: true, situacao: 'ATIVO' }) as any}
              canManage={canManage}
              onEdit={() => {
                const id = fichaId;
                setFichaId(null);
                setFichaAluno(null);
                const a = alunos?.find((al: Aluno) => al.id === id);
                openEdit(a || { id } as Aluno);
              }}
              onTransfer={() => setTransferAlunoId(fichaId)}
              onDeclaracao={async () => {
                try {
                  const res = await api.get(
                    `/escolas/${escolaId}/alunos/${fichaId}/pdf/declaracao-matricula`,
                    { responseType: 'blob' },
                  );
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `declaracao-${fichaAluno?.nome}.pdf`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch {
                  toast.error('Erro ao gerar PDF');
                }
              }}
              onDeactivate={() => {
                const a = alunos?.find((al: Aluno) => al.id === fichaId);
                if (a) {
                  setFichaId(null);
                  setFichaAluno(null);
                  requestDelete(a);
                }
              }}
              onReactivate={() => {
                const a = alunos?.find((al: Aluno) => al.id === fichaId);
                if (a) void handleReactivate(a);
              }}
            />
          ) : undefined
        }
      >
        {fichaId && escolaId ? (
          <AlunoFichaDesktop
            alunoId={fichaId}
            escolaId={escolaId}
            canManage={canManage}
            onClose={() => { setFichaId(null); setFichaAluno(null); }}
          />
        ) : null}
      </Modal>

      <Modal
        open={!!transferAlunoId}
        onClose={() => { setTransferAlunoId(null); setTransferMotivo(''); setTransferEscola(''); }}
        title="Transferir aluno"
        size="default"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: '0.85rem', color: '#64748b', margin: 0 }}>
            Transferir <strong>{fichaAluno?.nome || 'aluno'}</strong> para outra escola.
          </p>
          <div className="form-group">
            <label className="form-label">Motivo da transferência *</label>
            <textarea
              className="form-input"
              value={transferMotivo}
              onChange={(e) => setTransferMotivo(e.target.value)}
              rows={2}
              placeholder="Mudança de cidade, preferência familiar..."
            />
          </div>
          <div className="form-group">
            <label className="form-label">Escola destino (opcional)</label>
            <input
              className="form-input"
              value={transferEscola}
              onChange={(e) => setTransferEscola(e.target.value)}
              placeholder="Nome da escola destino"
            />
          </div>
        </div>
        <div className="modal-footer" style={{ marginTop: 16 }}>
          <button type="button" className="btn-cancel" onClick={() => setTransferAlunoId(null)}>Cancelar</button>
          <button
            type="button"
            className="btn-save"
            disabled={!transferMotivo.trim() || transferLoading}
            onClick={async () => {
              if (!transferAlunoId || !escolaId) return;
              setTransferLoading(true);
              try {
                await api.patch(ROUTES.alunos.findOne(escolaId, transferAlunoId) + '/transferir', {
                  motivo: transferMotivo,
                  escolaDestino: transferEscola || undefined,
                });
                toast.success('Aluno transferido');
                setTransferAlunoId(null);
                setFichaId(null);
                setFichaAluno(null);
                refetch();
              } catch {
                toast.error('Erro ao transferir');
              } finally {
                setTransferLoading(false);
              }
            }}
          >
            {transferLoading ? 'Transferindo...' : 'Confirmar'}
          </button>
        </div>
      </Modal>

      <ConfirmModal
        open={!!deleteConfirm}
        title="Desativar aluno"
        message={
          deleteConfirm
            ? `Deseja desativar "${deleteConfirm.nome}"? O aluno ficará com status inativo na listagem.`
            : ''
        }
        confirmLabel="Desativar"
        cancelLabel="Cancelar"
        variant="danger"
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}

